/*
  Месяцы с начислениями из двух источников: список, отметки и выбор человека.

  Решение владельца 01.10.2026 («выбор человека на вкладке»): перенос не пишет
  месяц, где в портфеле уже есть начисления, а файл называет услуги иначе, -
  человек решает сам: оставить записанные, заменить начислениями файла или
  дописать к записанным. Здесь состояние и запросы, разметка - в
  `MonthChoices.jsx`.

  Отметки - по образцу расхождений с портфелем (`useDivergences`), с уже
  исправленным поведением:
  - решения действуют только на ПОКАЗАННЫЕ строки: число в кнопках и то, что
    уходит на сервер, - отмеченные из видимых;
  - перечитывание списка не сбрасывает отметок человека: исчезнувшая строка
    уходит из отметок, остальные остаются как он их оставил;
  - заранее не отмечается НИЧЕГО, в том числе новые строки: здесь система
    ничего не предлагает сама - выбор целиком за человеком.

  Список перечитывается, когда меняется `revision` вкладки: прочитан новый
  файл или кончился перенос.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../../api/client.js'

export const PAGE = 20

const EMPTY = { items: [], shown: PAGE, chosen: [] }
const IDLE = { busy: false, error: null, loadError: null }

export default function useMonthChoices(revision, onDecided) {
  const [list, setList] = useState(EMPTY)
  const [state, setState] = useState(IDLE)

  const reload = useCallback(async () => {
    try {
      const found = await api.uploadMonths()
      const present = new Set(found.map((item) => item.id))
      setList((current) => ({
        ...current,
        items: found,
        chosen: current.chosen.filter((id) => present.has(id)),
      }))
      setState((current) => ({ ...current, loadError: null }))
    } catch (failure) {
      setState((current) => ({ ...current, loadError: failure }))
    }
  }, [])

  useEffect(() => {
    reload()
  }, [revision, reload])

  const visible = list.items.slice(0, list.shown)
  const chosenRows = visible.filter((item) => list.chosen.includes(item.id))
  const chosen = chosenRows.map((item) => item.id)
  // Замена запрещена, если среди отмеченных есть месяц, вошедший в поданные или
  // готовые к подаче документы: сервер отказал бы всему решению.
  const blocked = chosenRows.some((item) => !item.can_replace)

  const showMore = (count) => setList((current) => ({ ...current, shown: current.shown + count }))

  const toggle = (id) =>
    setList((current) => ({
      ...current,
      chosen: current.chosen.includes(id)
        ? current.chosen.filter((item) => item !== id)
        : [...current.chosen, id],
    }))

  // Отметить или снять все ПОКАЗАННЫЕ строки - не весь список.
  const chooseShown = (on) =>
    setList((current) => {
      const shownIds = current.items.slice(0, current.shown).map((item) => item.id)
      const rest = current.chosen.filter((id) => !shownIds.includes(id))
      return { ...current, chosen: on ? [...rest, ...shownIds] : rest }
    })

  // Ответ - удалось ли: окно подтверждения замены закрывается только при успехе.
  const decide = async (action) => {
    setState({ ...IDLE, busy: true })
    try {
      await api.decideMonths(chosen, action)
      setState(IDLE)
      await reload()
      onDecided?.()
      return true
    } catch (failure) {
      setState({ ...IDLE, error: failure })
      return false
    }
  }

  const forgetError = () => setState((current) => ({ ...current, error: null }))

  return {
    items: list.items,
    visible,
    chosen,
    blocked,
    showMore,
    toggle,
    chooseShown,
    decide,
    forgetError,
    ...state,
  }
}
